import React, { useState } from 'react';
import {
  IAuditoriaAdiantamentoSociosResultado,
  IItemAdiantamentoSocios,
} from '../interfaces/IAuditoriaAdiantamentoSocios';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaAdiantamentoSociosResultado;
}


const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-279: drill-down a nível de lançamento — todo ERRO precisa mostrar não só o saldo agregado,
// mas os lançamentos reais que compõem o saldo sem lastro em Lucros Acumulados.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemAdiantamentoSocios }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaAdiantamentoSociosDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const adiantamentosSemLastro = resultado.adiantamentosSemLastro || [];
  const adiantamentosConciliados = resultado.adiantamentosConciliados || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Adiantamentos a Sócios (Caracterização de Remuneração) — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>INSS patronal 20% + IRRF tabela progressiva + Multa ECD potencial (5% limitada a 1% da receita bruta do período — Art. 12, II, Lei 8.218/1991).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Adiantamentos a sócios sem lastro suficiente em Lucros Acumulados.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada à caracterização de remuneração.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Caracterização de Remuneração:</strong> {resultado.fundamentacaoCaracterizacaoRemuneracao}</div>
        </div>
      )}

      {/* Adiantamentos sem lastro em lucro */}
      <div className='auditoria-card'>
        <h3>Adiantamentos a Sócios sem Lastro em Lucros Acumulados</h3>
        <p className='text-muted'>
          Contas de Adiantamento a Sócios (Ativo) cujo saldo final supera o saldo líquido de Lucros
          Acumulados disponível para distribuição. O excedente é tratado como remuneração (pró-labore)
          caracterizada, sujeita a INSS patronal, IRRF e Multa ECD.
        </p>

        {adiantamentosSemLastro.length === 0 && (
          <div className='text-muted mt-4'>Nenhum adiantamento a sócios sem lastro identificado no período.</div>
        )}

        {adiantamentosSemLastro.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th className='text-end'>Saldo Adiantamento</th>
                  <th className='text-end'>Lucros Acumulados</th>
                  <th className='text-end'>Valor Tributável</th>
                  <th className='text-end'>INSS (20%)</th>
                  <th className='text-end'>IRRF</th>
                  <th className='text-end'>Multa ECD Potencial</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosSemLastro.map((item: IItemAdiantamentoSocios, idx) => {
                  const chave = `as-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td className='text-end'>{formatCurrency(item.saldoAdiantamento)}</td>
                        <td className='text-end'>{formatCurrency(item.saldoLucrosAcumulados)}</td>
                        <td className='text-end'>{formatCurrency(item.valorTributavel)}</td>
                        <td className='text-end'>{formatCurrency(item.valorInss)}</td>
                        <td className='text-end'>{formatCurrency(item.valorIrrf)}</td>
                        <td className='text-end'>{formatCurrency(item.multaEcd)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={8} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Adiantamentos conciliados — visibilidade de que a auditoria rodou */}
      {adiantamentosConciliados.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Adiantamentos a Sócios Verificados e Conciliados
          </h3>
          <p className='text-muted'>
            Contas auditadas com lastro suficiente em Lucros Acumulados, ou sem saldo de adiantamento no período.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th className='text-end'>Saldo Adiantamento</th>
                  <th className='text-end'>Lucros Acumulados</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosConciliados.map((item: IItemAdiantamentoSocios, idx) => {
                  const chave = `asc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td className='text-end'>{formatCurrency(item.saldoAdiantamento)}</td>
                        <td className='text-end'>{formatCurrency(item.saldoLucrosAcumulados)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaAdiantamentoSociosDetalhe;
